import moment from 'moment';
import omit from 'lodash.omit';
import isUnionWeeklyScheduleCode from 'common/utilities/isUnionWeeklySchedule';

const termsOfEmploymentKeys = [
  'rate',
  'rateDistant',
  'guaranteedHours6thDay',
  'guaranteedHours7thDay',
  'guaranteedHours6thDayDistant',
  'guaranteedHours7thDayDistant',
  'payIdleDaysDistant',
  'payGoldAt',
  'payGoldAtDistant',
  'payAtScale',
  'payAtScaleDistant',
  'idleAtScaleDistant',
  'goldAtScale',
  'goldAtScaleDistant',
  'idleLocationSeventhDayRatePerHour',
  'idleLocationSixthDayRatePerHour',
  'locationRatePerHour',
  'locationRatePerWeek',
  'studioGuaranteedHours',
  'studioRatePerHour',
  'studioRatePerWeek',
  'dailyRate',
  'weeklyRate',
  'ratePerHour',
  'overtimeRatePerHour',
  'locationGuaranteedHours',
  'guaranteedHours',
  'numberOfDaysPerWorkWeek',
];

const allowancesKeys = [
  'boxRentalAllowance',
  'carAllowance',
  'computerRentalAllowance',
  'housingAllowance',
  'mobilePhoneAllowance',
  'perDiemAllowance',
];

const FORM_DATA_TO_ACCOUNT_CODE_KEY_MAPPING = {
  location: {
    formDataKey: 'termsOfEmployment',
    formDataSubKeys: [
      'idleLocationSeventhDayRatePerHour',
      'idleLocationSixthDayRatePerHour',
      'locationRatePerHour',
      'locationRatePerWeek',
      'rateDistant',
    ],
  },
  studio: {
    formDataKey: 'termsOfEmployment',
    formDataSubKeys: [
      'studioGuaranteedHours',
      'studioRatePerHour',
      'studioRatePerWeek',
      'rate',
    ],
  },
  daily: {
    formDataKey: 'termsOfEmployment',
    formDataSubKeys: ['dailyRate'],
  },
  weekly: {
    formDataKey: 'termsOfEmployment',
    formDataSubKeys: ['weeklyRate'],
  },
  hourly: {
    formDataKey: 'termsOfEmployment',
    formDataSubKeys: ['ratePerHour'],
  },
  box: {
    formDataKey: 'allowances',
    formDataSubKeys: ['boxRentalAllowance'],
  },
  car: {
    formDataKey: 'allowances',
    formDataSubKeys: ['carAllowance'],
  },
  computer: {
    formDataKey: 'allowances',
    formDataSubKeys: ['computerRentalAllowance'],
  },
  housing: {
    formDataKey: 'allowances',
    formDataSubKeys: ['housingAllowance'],
  },
  mobile: {
    formDataKey: 'allowances',
    formDataSubKeys: ['mobilePhoneAllowance'],
  },
  perDiem: {
    formDataKey: 'allowances',
    formDataSubKeys: ['perDiemAllowance'],
  },
};

const getIsAccountCodeFilledIn = (formData, { value, lineItemKey }) => {
  if (!value) return false;
  const {
    [lineItemKey]: { formDataKey, formDataSubKeys },
  } = FORM_DATA_TO_ACCOUNT_CODE_KEY_MAPPING || {};
  const { [formDataKey]: formDataSubSet } = formData || {};
  return formDataSubKeys.some(subKey => {
    if (formDataKey === 'allowances') {
      const { [subKey]: { amount } = {} } = formDataSubSet || {};
      if (!amount) return false;
      return true;
    } else {
      const { [subKey]: value } = formDataSubSet || {};
      if (!value) return false;
      return true;
    }
  });
};

const formatAmount = value => (!!value && parseFloat(value)) || null;

const formatAllowances = allowance => ({
  ...allowance,
  amount: formatAmount(allowance.amount),
  duration: formatAmount(allowance.duration),
  cap: formatAmount(allowance.cap),
});

const formatTerms = terms => {
  const updatedTerms = { ...terms };
  Object.keys(terms).forEach(key => {
    if (termsOfEmploymentKeys.includes(key)) {
      updatedTerms[key] = formatAmount(terms[key]);
      delete updatedTerms['isPrcanRateTable'];
      delete updatedTerms['scaleRates'];
    }
    if (allowancesKeys.includes(key)) {
      updatedTerms[key] = formatAllowances(terms[key]);
    }
  });
  return updatedTerms;
};

// for Non Union schedules
const formatTermsV2 = terms => {
  const {
    rate,
    ratePerHour,
    guaranteedHours,
    overtimeRatePerHour,
    guaranteedHours6thDay,
    guaranteedHours7thDay,
    sixthDayPayMultiplication,
    seventhDayPayMultiplication,
  } = terms;

  const hourlyRate = formatAmount(ratePerHour);

  return {
    rate: hourlyRate || formatAmount(rate),
    rateOvertime: formatAmount(overtimeRatePerHour),
    guaranteedHours: parseFloat(guaranteedHours),
    guaranteedHours6thDay,
    guaranteedHours7thDay,
    sixthDayPayMultiplication,
    seventhDayPayMultiplication,
  };
};

// for union schedule A
const formatTermsUnionHourlyV2 = terms => {
  const { ratePerHour, overtimeRatePerHour, rate, guaranteedHours } = terms;
  const result = omit(terms, ['isTermsOfEmploymentV2']);
  const unionScheduleData = {
    rateDistant: formatAmount(terms?.rateDistant),
    payGoldAt: formatAmount(terms?.payGoldAt),
    payGoldAtDistant: formatAmount(terms?.payGoldAtDistant),
    payIdleDaysDistant: formatAmount(terms?.payIdleDaysDistant),
    rate: formatAmount(ratePerHour) || formatAmount(rate),
    rateOvertime: formatAmount(overtimeRatePerHour),
    guaranteedHours: parseFloat(guaranteedHours),
  };

  return {
    ...result,
    ...unionScheduleData,
  };
};

// for union weekly schedule
const formatTermsUnionWeeklyV2 = terms => {
  const unionScheduleData = {
    rate: formatAmount(terms?.rate),
    rateDistant: formatAmount(terms?.rateDistant),
    weeklyRate: formatAmount(terms?.weeklyRate),
    weeklyRateDistant: formatAmount(terms?.weeklyRateDistant),
    guaranteedHours: parseFloat(terms?.guaranteedHours),
    guaranteedHoursDistant: parseFloat(terms?.guaranteedHoursDistant),
    payGoldAt: formatAmount(terms?.payGoldAt),
    payGoldAtDistant: formatAmount(terms?.payGoldAtDistant),
    payIdleDaysDistant: formatAmount(terms?.payIdleDaysDistant),
    goldAtScale: terms?.goldAtScale,
    goldAtScaleDistant: terms?.goldAtScaleDistant,
    idleAtScaleDistant: terms?.idleAtScaleDistant,
    payAtScale: terms?.payAtScale,
    payAtScaleDistant: terms?.payAtScaleDistant,
  };
  return {
    ...unionScheduleData,
  };
};

const getNormalizedMutationPayload = formData => {
  const {
    offerDate = {},
    termsOfHire = {},
    termsOfEmployment = {},
    allowances = {},
    accountCodes = [],
    dealNotes = [],
    documents = [],
    documentFields = [],
  } = formData;
  const { startDateObject, sendDateObject, endDateObject } = offerDate || {};
  const { union, occupation, workSchedule } = termsOfHire || {};
  const { value: unionValue, label: unionLabel, isNonUnion } = union || {};
  const { value: occupationValue, label: occupationLabel } = occupation || {};
  const { value: workScheduleValue, label: workScheduleLabel } =
    workSchedule || {};
  const {
    isTermsOfEmploymentV2 = false,
    isPrcanRateTable = false,
  } = termsOfEmployment;
  const isUnionWeeklySchedule =
    isUnionWeeklyScheduleCode(workScheduleValue) &&
    !isNonUnion &&
    isTermsOfEmploymentV2;
  const isUnionScheduleA =
    workScheduleValue === 'A' && !isNonUnion && !isPrcanRateTable;
  const sanitizedTermsOfEmployment = formatTerms({ ...termsOfEmployment });
  let sanitizedTermsOfEmploymentV2;
  if (isUnionScheduleA) {
    sanitizedTermsOfEmploymentV2 = formatTermsUnionHourlyV2({
      ...termsOfEmployment,
    });
  } else if (isUnionWeeklySchedule) {
    sanitizedTermsOfEmploymentV2 = formatTermsUnionWeeklyV2({
      ...termsOfEmployment,
    });
    delete sanitizedTermsOfEmploymentV2.scaleRates;
  } else {
    sanitizedTermsOfEmploymentV2 = formatTermsV2({ ...termsOfEmployment });
  }
  delete sanitizedTermsOfEmployment.__typename;
  delete sanitizedTermsOfEmployment.scaleRateHash;

  delete sanitizedTermsOfEmploymentV2.__typename;
  delete sanitizedTermsOfEmploymentV2.scaleRateHash;
  delete sanitizedTermsOfEmploymentV2.tableErrors;

  const sanitizedAllowances = formatTerms({ ...allowances });
  const allowanceKeys = Object.keys(sanitizedAllowances);
  allowanceKeys.forEach(key => delete sanitizedAllowances[key].__typename);

  const sanitizedAccountCodes = [...accountCodes]
    .filter(accountCode => getIsAccountCodeFilledIn(formData, accountCode))
    .map(ac => {
      const updatedAccountCode = { ...ac, id: ac.accountCodeId };
      delete updatedAccountCode.accountCodeId;
      delete updatedAccountCode.__typename;
      return updatedAccountCode;
    });

  const currentDealNote = dealNotes.find(({ current }) => !!current);
  const newDealNote = currentDealNote && {
    value: currentDealNote.notes,
    timeStamp: moment().format('M/D/YYYY h:mm a'),
  };

  const formattedTermsOfHire = {
    ...termsOfHire,
    occupation: {
      code: occupationValue,
      description: occupationLabel,
    },
    union: {
      code: unionValue,
      description: unionLabel,
      isNonUnion: isNonUnion,
    },
    workSchedule: {
      code: workScheduleValue,
      description: workScheduleLabel,
    },
  };
  delete formattedTermsOfHire.workStateId;
  delete formattedTermsOfHire.hireStateId;

  // Coerce all document fields to string and remove __typenames
  const formattedDocumentFields = documentFields.map(({ id, value }) => ({
    id,
    value: `${value}`,
  }));

  const termsOfEmploymentTitle =
    isTermsOfEmploymentV2 || isUnionScheduleA || isUnionWeeklySchedule
      ? 'termsOfEmploymentV2'
      : 'termsOfEmployment';

  const mutationPayload = {
    offerDate: {
      startDate: startDateObject.format('YYYY-MM-DD'),
      sendDate: (sendDateObject && sendDateObject.format('YYYY-MM-DD')) || null,
      endDate: (!!endDateObject && endDateObject.format('YYYY-MM-DD')) || null,
    },
    termsOfHire: formattedTermsOfHire,
    [termsOfEmploymentTitle]:
      isTermsOfEmploymentV2 || isUnionScheduleA || isUnionWeeklySchedule
        ? sanitizedTermsOfEmploymentV2
        : sanitizedTermsOfEmployment,
    allowances: sanitizedAllowances,
    accountCodes: sanitizedAccountCodes,
    dealNote: newDealNote,
    documents,
    documentFields: formattedDocumentFields,
  };
  return mutationPayload;
};

export default getNormalizedMutationPayload;
